import { useId } from 'react';
import { CustomerEquipmentTypeResponse } from '@/types';
import { getAvailableTiers } from '@/lib/pricing';
import { useFormatPrice } from '@/hooks/useFormatPrice';
import { cn } from '@/lib/utils';
import { format, addDays, differenceInCalendarDays } from 'date-fns';

interface RentalDurationSelectorProps {
    product: CustomerEquipmentTypeResponse;
    onPickupDateChange?: (date: Date | null) => void;
    onReturnDateChange?: (date: Date | null) => void;
    pickupDate?: Date | null;
    returnDate?: Date | null;
    isNewItem?: boolean;
    className?: string;
}

/** Exact dates are required; rate bands explain the calculated price. */
export const RentalDurationSelector = ({ product, pickupDate, returnDate, onPickupDateChange, onReturnDateChange, isNewItem = false, className }: RentalDurationSelectorProps) => {
    const id = useId();
    const formatPrice = useFormatPrice();
    const tiers = getAvailableTiers(product, product, isNewItem);
    const days = pickupDate && returnDate ? differenceInCalendarDays(returnDate, pickupDate) : null;
    const tier = days != null ? tiers.find(option => days >= option.minDays && days <= option.maxDays) : undefined;
    const dateValue = (date?: Date | null) => date ? format(date, 'yyyy-MM-dd') : '';
    const parse = (value: string) => value ? new Date(`${value}T12:00:00`) : null;
    if (tiers.length === 0) return null;
    return (
        <div className={cn('space-y-4', className)}>
            <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <legend className="mb-3 text-base font-semibold">When will you collect and return it?</legend>
                <div>
                    <label htmlFor={`${id}-pickup`} className="mb-1 block text-sm font-medium">Pickup date</label>
                    <input id={`${id}-pickup`} type="date" required value={dateValue(pickupDate)} min={dateValue(new Date())}
                        onChange={event => onPickupDateChange?.(parse(event.target.value))}
                        className="min-h-11 w-full min-w-0 rounded-lg border border-border bg-white px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                </div>
                <div>
                    <label htmlFor={`${id}-return`} className="mb-1 block text-sm font-medium">Return date</label>
                    <input id={`${id}-return`} type="date" required value={dateValue(returnDate)} disabled={!pickupDate}
                        min={dateValue(pickupDate ? addDays(pickupDate, 1) : undefined)}
                        onChange={event => onReturnDateChange?.(parse(event.target.value))}
                        className="min-h-11 w-full min-w-0 rounded-lg border border-border bg-white px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60" />
                </div>
            </fieldset>
            <div role="status" className="text-sm text-foreground">
                {tier && days ? <p><strong>{formatPrice(tier.price)} rental total</strong> · {days} {days === 1 ? 'day' : 'days'} <span className="block text-muted-foreground">{tier.tierName} rate</span></p>
                    : <p>{days != null ? 'No rental price is available for these dates. Choose other dates or contact us.' : 'Choose both dates to see your rental total.'}</p>}
            </div>
            <details className="border-t border-border pt-2">
                <summary className="flex min-h-11 cursor-pointer items-center text-sm font-semibold text-primary">View rental rates</summary>
                <dl className="grid grid-cols-2 gap-3 py-2 text-sm sm:grid-cols-3">
                    {tiers.map(option => <div key={option.tierName}><dt>{option.tierName}</dt><dd className="font-semibold">{formatPrice(option.price)}</dd></div>)}
                </dl>
            </details>
        </div>
    );
};

export default RentalDurationSelector;
